import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from '@/lib/analytics';
import { ArrowRight, Check, Minus, Building2, Network } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

// Two-tier plans page, built audience-first to avoid the "Essentials = cheap
// Oracle option" confusion. Enterprise is the hero (Oracle Fusion/EBS + complex
// finance); Essentials is explicitly a different product for mid-market teams
// without ERP-native integration (Yardi, Dynamics). No prices — pricing posture
// is still an open owner decision; CTAs route to a demo.

type Cell = string | boolean;
const COMPARISON: { feature: string; essentials: Cell; enterprise: Cell }[] = [
  {
    feature: 'Best fit',
    essentials: 'Mid-market teams; simpler AP setups',
    enterprise: 'Oracle Fusion & EBS; complex finance teams',
  },
  {
    feature: 'ERP integration',
    essentials: 'Standalone automation — no ERP-native validation',
    enterprise: 'Native Oracle EBS & Fusion (+ NetSuite, SAP, Dynamics, Yardi), real-time validation',
  },
  {
    feature: 'Typical ERP',
    essentials: 'Yardi, Microsoft Dynamics',
    enterprise: 'Oracle Fusion, Oracle E-Business Suite',
  },
  {
    feature: 'Approvals',
    essentials: 'Notification / flag — approvers can browse all invoices',
    enterprise: 'User-specific queues — approvers see only their assigned invoices',
  },
  {
    feature: 'Workflow routing',
    essentials: 'Single-level, simple',
    enterprise: 'Multi-level, rules-based',
  },
  {
    feature: 'Access control',
    essentials: 'Basic',
    enterprise: 'Role-based access (RBAC) + just-in-time access',
  },
  { feature: 'Azure AD single sign-on', essentials: false, enterprise: true },
  { feature: 'ERP supplier sync', essentials: false, enterprise: true },
  { feature: 'AI email exception prevention', essentials: false, enterprise: true },
  {
    feature: 'Audit trail',
    essentials: 'Standard',
    enterprise: 'Full, exportable, SOX-ready',
  },
];

const renderCell = (value: Cell) => {
  if (value === true) return <Check className="w-5 h-5 text-accent mx-auto" aria-label="Included" />;
  if (value === false) return <Minus className="w-5 h-5 text-gray-300 mx-auto" aria-label="Not included" />;
  return <span className="text-sm text-gray-600">{value}</span>;
};

const Plans = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="EZ Cloud Plans — Enterprise & Essentials | EZ Cloud"
        description="Two EZ Cloud tiers for two buyers: Enterprise for Oracle Fusion & EBS and complex finance teams, and Essentials for mid-market AP automation without deep ERP integration (Yardi, Dynamics)."
        canonical="https://ezcloud.ai/plans"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Plans', url: 'https://ezcloud.ai/plans' },
        ]}
      />
      <Navigation />
      <Breadcrumbs />

      <main className="container mx-auto px-4 py-16 max-w-5xl">
        <header className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Two tiers, two different buyers
          </h1>
          <p className="text-lg text-gray-600">
            EZ Cloud comes in two editions built for genuinely different needs — not a price ladder.
            Enterprise is the deep, ERP-native platform for Oracle finance teams. Essentials is a
            streamlined edition for mid-market teams that want automation without ERP-native integration.
          </p>
        </header>

        {/* Audience cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {/* Enterprise — hero */}
          <Card className="relative p-8 bg-white border-2 border-accent shadow-md flex flex-col">
            <span className="absolute -top-3 left-8 bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wide rounded-full px-3 py-1">
              Recommended for Oracle
            </span>
            <div className="flex items-center gap-2 text-primary mb-3">
              <Network className="w-5 h-5" aria-hidden />
              <span className="text-sm font-semibold uppercase tracking-wide">Enterprise</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              For Oracle Fusion &amp; EBS and complex finance teams
            </h2>
            <p className="text-gray-600 mb-5 flex-grow">
              The full EZ Cloud platform: native Oracle integration with real-time validation,
              user-specific approval queues, multi-level workflows, RBAC and SSO, ERP supplier sync,
              and AI email exception prevention. This is the EZ Cloud you see across the rest of the site.
            </p>
            <Button variant="accent" className="w-full" onClick={() => openCalendlyDemo()}>
              Book an Oracle AP demo
              <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
            </Button>
          </Card>

          {/* Essentials */}
          <Card className="p-8 bg-white border border-gray-200 shadow-sm flex flex-col">
            <div className="flex items-center gap-2 text-gray-500 mb-3">
              <Building2 className="w-5 h-5" aria-hidden />
              <span className="text-sm font-semibold uppercase tracking-wide">Essentials</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              For mid-market teams that want simple AP automation
            </h2>
            <p className="text-gray-600 mb-5 flex-grow">
              A streamlined edition with lightweight, notification-based approvals and no ERP-native
              integration. Best for simpler setups — for example, teams on <strong>Yardi</strong> or{' '}
              <strong>Microsoft Dynamics</strong> that want fast automation without enterprise workflow
              and ERP validation. <span className="text-gray-500">Not intended for Oracle Fusion or EBS — those run Enterprise.</span>
            </p>
            <Button variant="outline" className="w-full" onClick={() => openCalendlyDemo()}>
              Talk to us about Essentials
              <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
            </Button>
          </Card>
        </div>

        {/* Which is right router */}
        <Card className="p-6 bg-gray-50 border border-gray-200 mb-16">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 text-center">Which is right for you?</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <Network className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
              <p className="text-gray-700">
                <span className="font-semibold">Choose Enterprise</span> if you run Oracle Fusion or EBS,
                need ERP-native validation, structured approval queues, RBAC/SSO, or operate in healthcare,
                public sector, or another audit-heavy environment.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden />
              <p className="text-gray-700">
                <span className="font-semibold">Choose Essentials</span> if you're a mid-market team on
                Yardi or Dynamics that wants quick AP automation and lightweight approvals, without deep
                ERP integration.
              </p>
            </div>
          </div>
        </Card>

        {/* Comparison table */}
        <section aria-labelledby="compare">
          <h2 id="compare" className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            Compare editions
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-4 text-sm font-semibold text-gray-900 w-1/3">Capability</th>
                  <th className="p-4 text-sm font-semibold text-gray-900 text-center">Essentials</th>
                  <th className="p-4 text-sm font-semibold text-primary text-center">
                    Enterprise <span className="text-accent">·</span> Oracle
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.feature} className={i % 2 ? 'bg-white' : 'bg-gray-50/40'}>
                    <td className="p-4 text-sm font-medium text-gray-900 align-top">{row.feature}</td>
                    <td className="p-4 text-center align-top">{renderCell(row.essentials)}</td>
                    <td className="p-4 text-center align-top">{renderCell(row.enterprise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">
            HACOV, our public Yardi case study, runs EZ Cloud Essentials.
          </p>
        </section>

        {/* CTA */}
        <section className="mt-16 text-center border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Not sure which fits?</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Tell us your ERP and how your approvals work today, and we'll point you to the right edition —
            no pressure.
          </p>
          <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
            Book a demo
            <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
          </Button>
        </section>
      </main>
    </div>
  );
};

export default Plans;
